import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatError } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Time12Input from "@/components/Time12Input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Home, User, Pencil, Power, Plus, ArrowRightLeft, Merge, Undo2, GitBranch } from "lucide-react";

function MoveMember({ member, households, onDone }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(member.household_id);
  const others = households.filter(h => h.id !== member.household_id && h.active);
  const submit = async () => {
    if (target === member.household_id) return setOpen(false);
    try {
      await api.patch(`/members/${member.id}`, { ...member, household_id: target });
      toast.success("Moved — all dinners and ratings preserved");
      onDone(); setOpen(false);
    } catch (e) { toast.error(formatError(e)); }
  };
  if (others.length === 0) return null;
  return (
    <>
      <Button size="icon" variant="ghost" onClick={()=>setOpen(true)} data-testid="move-member-btn" title="Move to another household"><ArrowRightLeft className="w-3 h-3"/></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Move {member.first_name} {member.last_name}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">All past dinners, ratings, and comments stay with {member.first_name}.</p>
          <Label>New household</Label>
          <select value={target} onChange={e=>setTarget(e.target.value)} className="w-full border rounded-md h-10 px-3 bg-background" data-testid="move-member-select">
            <option value={member.household_id}>— stay in current —</option>
            {others.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="confirm-move-btn">Move</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function HouseholdModal({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(initial || { name:"", street:"", city:"", state:"", zip_code:"", latitude:"", longitude:"", active:true });
  useEffect(() => { if (initial) setF({...initial, latitude: initial.latitude?.toString() ?? "", longitude: initial.longitude?.toString() ?? ""}); else setF({ name:"", street:"", city:"", state:"", zip_code:"", latitude:"", longitude:"", active:true }); }, [initial, open]);
  const save = async () => {
    try {
      const body = { ...f, latitude: parseFloat(f.latitude) || 0, longitude: parseFloat(f.longitude) || 0 };
      if (initial?.id) await api.patch(`/households/${initial.id}`, body);
      else await api.post("/households", body);
      toast.success(initial?.id ? "Household updated" : "Household added");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit household" : "Add household"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Name</Label><Input data-testid="household-name-input" value={f.name} onChange={e=>setF({...f, name:e.target.value})}/></div>
          <div><Label>Street</Label><Input value={f.street} onChange={e=>setF({...f, street:e.target.value})}/></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>City</Label><Input value={f.city} onChange={e=>setF({...f, city:e.target.value})}/></div>
            <div><Label>State</Label><Input value={f.state} onChange={e=>setF({...f, state:e.target.value})}/></div>
            <div><Label>ZIP</Label><Input value={f.zip_code} onChange={e=>setF({...f, zip_code:e.target.value})}/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Latitude</Label><Input value={f.latitude} onChange={e=>setF({...f, latitude:e.target.value})}/></div>
            <div><Label>Longitude</Label><Input value={f.longitude} onChange={e=>setF({...f, longitude:e.target.value})}/></div>
          </div>
          <p className="text-xs text-muted-foreground">Leave lat/lng blank to auto-geocode from the address (Google Maps on).</p>
          <label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={v=>setF({...f, active: v})}/> <span className="text-sm">Active</span></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button data-testid="household-save-btn" onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemberModal({ open, onOpenChange, initial, households, onSaved }) {
  const empty = { first_name:"", last_name:"", email:"", phone:"", household_id: households[0]?.id || "", active:true, earliest_time:"17:30", preferred_time:"18:30", latest_time:"19:30", temporary:false };
  const [f, setF] = useState(initial || empty);
  useEffect(() => { setF(initial || {...empty, household_id: households[0]?.id || ""}); }, [initial, open, households]);
  const save = async () => {
    try {
      const body = { ...f, email: f.email || null, phone: f.phone || null };
      if (initial?.id) await api.patch(`/members/${initial.id}`, body);
      else await api.post("/members", body);
      toast.success(initial?.id ? "Member updated" : "Member added");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit member" : "Add member"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>First name</Label><Input data-testid="member-first-input" value={f.first_name} onChange={e=>setF({...f, first_name:e.target.value})}/></div>
            <div><Label>Last name</Label><Input value={f.last_name} onChange={e=>setF({...f, last_name:e.target.value})}/></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={f.email || ""} onChange={e=>setF({...f, email:e.target.value})}/></div>
          <div><Label>Phone</Label><Input value={f.phone || ""} onChange={e=>setF({...f, phone:e.target.value})}/></div>
          <div>
            <Label>Household</Label>
            <select value={f.household_id} onChange={e=>setF({...f, household_id:e.target.value})} className="w-full border rounded-md h-10 px-3 bg-background" data-testid="member-household-select">
              {households.map(h=><option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Earliest</Label><Time12Input value={f.earliest_time} onChange={v=>setF({...f, earliest_time:v})} testId="member-earliest-time"/></div>
            <div><Label>Preferred</Label><Time12Input value={f.preferred_time} onChange={v=>setF({...f, preferred_time:v})} testId="member-preferred-time"/></div>
            <div><Label>Latest</Label><Time12Input value={f.latest_time} onChange={v=>setF({...f, latest_time:v})} testId="member-latest-time"/></div>
          </div>
          <label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={v=>setF({...f, active:v})}/> <span className="text-sm">Active</span></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button data-testid="member-save-btn" onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GroupPage() {
  const [households, setHouseholds] = useState([]);
  const [members, setMembers] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [hhOpen, setHhOpen] = useState(false); const [hhEdit, setHhEdit] = useState(null);
  const [mOpen, setMOpen] = useState(false); const [mEdit, setMEdit] = useState(null);
  const [mergeCandidates, setMergeCandidates] = useState([]);
  const [mergePick, setMergePick] = useState(null); // {a, b, reason}
  const [deactivated, setDeactivated] = useState({households:[], members:[], restaurants:[]});

  const load = () => Promise.all([
    api.get("/households").then(r=>r.data),
    api.get("/members").then(r=>r.data),
    api.get("/households/merge-candidates").then(r=>r.data).catch(()=>[]),
    api.get("/deactivated").then(r=>r.data).catch(()=>({households:[],members:[],restaurants:[]})),
  ]).then(([h,m,mc,d])=>{ setHouseholds(h); setMembers(m); setMergeCandidates(mc); setDeactivated(d); });
  useEffect(()=>{ load(); }, []);

  const toggleActive = async (kind, item) => {
    try {
      if (kind === "household") await api.patch(`/households/${item.id}`, { ...item, active: !item.active });
      else await api.patch(`/members/${item.id}`, { ...item, active: !item.active });
      toast.success(!item.active ? "Reactivated" : "Deactivated — history preserved. Undo from the tray below.");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const restoreHousehold = async (h) => { try { await api.patch(`/households/${h.id}`, { ...h, active: true }); toast.success("Restored"); load(); } catch(e){toast.error(formatError(e));} };
  const restoreMember = async (m) => { try { await api.patch(`/members/${m.id}`, { ...m, active: true }); toast.success("Restored"); load(); } catch(e){toast.error(formatError(e));} };
  const restoreRestaurant = async (r) => { try { await api.patch(`/restaurants/${r.id}`, { active: true }); toast.success("Restored"); load(); } catch(e){toast.error(formatError(e));} };
  const unmergeHousehold = async (h) => {
    try {
      const { data } = await api.post(`/households/${h.id}/unmerge`);
      const stayed = data.stayed_on_target_count;
      toast.success(`Unmerged · ${data.moved_back_count} member(s) restored${stayed ? ` · ${stayed} stayed on target (moved after merge)` : ""}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const doMerge = async (overrides) => {
    if (!mergePick) return;
    try {
      const { data } = await api.post(`/households/${mergePick.a.id}/merge-into/${mergePick.b.id}`, { overrides: overrides || {} });
      toast.success(`Merged: moved ${data.moved_members} member(s) into ${data.target.name}`);
      setMergePick(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const visibleHouseholds = households.filter(h => showInactive || h.active);
  const membersOfHousehold = (hid) => members.filter(m => m.household_id === hid && (showInactive || m.active));
  const anyDeactivated = deactivated.households.length + deactivated.members.length + deactivated.restaurants.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Group management</div>
          <h1 className="font-serif text-3xl sm:text-4xl">Households & members</h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="show-inactive-toggle"/> Show inactive
          </label>
          <Button variant="outline" onClick={()=>{setHhEdit(null); setHhOpen(true);}} data-testid="add-household-btn"><Plus className="w-4 h-4 mr-1"/>Household</Button>
          <Button onClick={()=>{setMEdit(null); setMOpen(true);}} data-testid="add-member-btn"><Plus className="w-4 h-4 mr-1"/>Member</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Deactivating a household or member preserves all past dinners, ratings, and comments. Nothing is deleted.</p>

      {mergeCandidates.length > 0 && (
        <Card className="p-5 border-amber-400 bg-amber-50/50 dark:bg-amber-900/10" data-testid="merge-candidates-banner">
          <div className="flex items-center gap-2 overline mb-2"><Merge className="w-4 h-4"/>Possible household duplicates</div>
          <div className="space-y-2">
            {mergeCandidates.map((p) => (
              <div key={`${p.a.id}-${p.b.id}`} className="flex items-center justify-between gap-3 text-sm p-3 rounded-lg bg-background border">
                <div className="flex-1">
                  <div><strong>{p.a.name}</strong> <span className="text-xs text-muted-foreground">· {p.a_members} member{p.a_members===1?"":"s"}</span></div>
                  <div className="text-xs mono text-muted-foreground">{p.a.street}, {p.a.city}</div>
                </div>
                <div className="text-xs text-muted-foreground text-center">↔<br/>{p.reason}</div>
                <div className="flex-1 text-right">
                  <div><strong>{p.b.name}</strong> <span className="text-xs text-muted-foreground">· {p.b_members} member{p.b_members===1?"":"s"}</span></div>
                  <div className="text-xs mono text-muted-foreground">{p.b.street}, {p.b.city}</div>
                </div>
                <Button size="sm" onClick={()=>setMergePick(p)} data-testid="review-merge-btn">Review merge</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4" data-testid="households-tree-container">
        {visibleHouseholds.map(h => (
          <Card key={h.id} data-testid="household-card" className={`p-5 ${!h.active?"opacity-60":""}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-primary"/>
                <div>
                  <h3 className="font-serif text-xl">{h.name} {!h.active && <span className="text-xs mono text-muted-foreground">(inactive)</span>}</h3>
                  <div className="text-xs mono text-muted-foreground">{h.street}, {h.city}, {h.state} {h.zip_code}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={()=>{setHhEdit(h); setHhOpen(true);}} data-testid="edit-household-btn"><Pencil className="w-4 h-4"/></Button>
                <Button size="icon" variant="ghost" onClick={()=>toggleActive("household", h)} data-testid="toggle-household-active-btn" title={h.active?"Deactivate":"Reactivate"}><Power className="w-4 h-4"/></Button>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              {membersOfHousehold(h.id).map(m => (
                <div key={m.id} data-testid="member-item-pill" className={`flex items-center justify-between rounded-md px-2 py-1 hover:bg-secondary/60 ${!m.active?"opacity-60":""}`}>
                  <div className="flex items-center gap-2 text-sm"><User className="w-3 h-3"/>{m.first_name} {m.last_name}{!m.active && <span className="text-xs text-muted-foreground">(inactive)</span>}{m.temporary && <span className="text-xs text-amber-700 ml-1">(temp)</span>}</div>
                  <div className="flex gap-1">
                    <MoveMember member={m} households={households} onDone={load}/>
                    <Button size="icon" variant="ghost" onClick={()=>{setMEdit(m); setMOpen(true);}} data-testid="edit-member-btn"><Pencil className="w-3 h-3"/></Button>
                    <Button size="icon" variant="ghost" onClick={()=>toggleActive("member", m)} data-testid="toggle-member-active-btn" title={m.active?"Deactivate":"Reactivate"}><Power className="w-3 h-3"/></Button>
                  </div>
                </div>
              ))}
              {membersOfHousehold(h.id).length === 0 && <div className="text-xs text-muted-foreground">No {showInactive?"":"active "}members</div>}
            </div>
          </Card>
        ))}
      </div>

      {anyDeactivated > 0 && (
        <Card className="p-5" data-testid="recently-deactivated-tray">
          <div className="flex items-center gap-2 overline mb-3"><Undo2 className="w-4 h-4"/>Recently deactivated · last 14 days</div>
          <div className="space-y-2">
            {deactivated.households.map(h => (
              <RestoreRow key={h.id} label={`Household: ${h.name}`} sub={`${h.street}, ${h.city}${h.merged_into ? " · merged into another household" : ""}`} when={h.deactivated_at}
                          onRestore={()=>restoreHousehold(h)}
                          onUnmerge={h.merged_into ? ()=>unmergeHousehold(h) : null}/>
            ))}
            {deactivated.members.map(m => (
              <RestoreRow key={m.id} label={`Member: ${m.first_name} ${m.last_name}`} sub={households.find(h=>h.id===m.household_id)?.name || "unknown household"} when={m.deactivated_at} onRestore={()=>restoreMember(m)}/>
            ))}
            {deactivated.restaurants.map(r => (
              <RestoreRow key={r.id} label={`Restaurant: ${r.name}`} sub={`${r.city}, ${r.state}`} when={r.deactivated_at} onRestore={()=>restoreRestaurant(r)}/>
            ))}
          </div>
        </Card>
      )}

      <HouseholdModal open={hhOpen} onOpenChange={setHhOpen} initial={hhEdit} onSaved={load}/>
      <MemberModal open={mOpen} onOpenChange={setMOpen} initial={mEdit} households={households} onSaved={load}/>

      <Dialog open={!!mergePick} onOpenChange={(o)=>{ if(!o) setMergePick(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Merge households</DialogTitle></DialogHeader>
          {mergePick && <MergePreview pair={mergePick} members={members} onCancel={()=>setMergePick(null)} onMerge={doMerge}/>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MERGE_FIELDS = [
  {k:"name", label:"Name"},
  {k:"street", label:"Street"},
  {k:"city", label:"City"},
  {k:"state", label:"State"},
  {k:"zip_code", label:"ZIP"},
  {k:"latitude", label:"Latitude"},
  {k:"longitude", label:"Longitude"},
];

function MergePreview({ pair, members, onCancel, onMerge }) {
  // Default winner per field: prefer target (b), but for empty target take source (a)
  const [picks, setPicks] = useState(() => {
    const p = {};
    MERGE_FIELDS.forEach(({k}) => {
      const a = pair.a[k], b = pair.b[k];
      p[k] = (b === "" || b === null || b === undefined) && (a !== "" && a !== null && a !== undefined) ? "a" : "b";
    });
    return p;
  });
  const movingMembers = members.filter(m => m.household_id === pair.a.id && m.active);
  const setPick = (k, side) => setPicks(p => ({...p, [k]: side}));
  const overrides = () => {
    const out = {};
    MERGE_FIELDS.forEach(({k}) => { if (picks[k] === "a") out[k] = pair.a[k]; });
    return out;
  };
  const differs = (k) => (pair.a[k] ?? "") !== (pair.b[k] ?? "");
  return (
    <div className="space-y-4 text-sm">
      <p className="text-xs text-muted-foreground">Pick the winning value for each field. The source household will be deactivated afterwards (retrievable from the Recently deactivated tray). All members and their history move to the target.</p>
      <div className="rounded-lg border overflow-hidden" data-testid="merge-preview-table">
        <div className="grid grid-cols-3 bg-secondary/60 text-xs mono uppercase tracking-widest text-muted-foreground">
          <div className="p-2">Field</div>
          <div className="p-2 border-l">Source · {pair.a.name}</div>
          <div className="p-2 border-l">Target · {pair.b.name}</div>
        </div>
        {MERGE_FIELDS.map(({k, label}) => (
          <div key={k} className={`grid grid-cols-3 border-t ${differs(k) ? "" : "opacity-60"}`}>
            <div className="p-2 text-xs mono">{label}</div>
            <div className={`p-2 border-l cursor-pointer ${picks[k]==="a"?"bg-primary/10":""}`} onClick={()=>setPick(k, "a")} data-testid={`merge-cell-${k}-a`}>
              <input type="radio" checked={picks[k]==="a"} onChange={()=>setPick(k, "a")} className="mr-2"/>
              <span className={picks[k]==="a"?"font-semibold":""}>{String(pair.a[k] ?? "—")}</span>
            </div>
            <div className={`p-2 border-l cursor-pointer ${picks[k]==="b"?"bg-primary/10":""}`} onClick={()=>setPick(k, "b")} data-testid={`merge-cell-${k}-b`}>
              <input type="radio" checked={picks[k]==="b"} onChange={()=>setPick(k, "b")} className="mr-2"/>
              <span className={picks[k]==="b"?"font-semibold":""}>{String(pair.b[k] ?? "—")}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border p-3">
        <div className="text-xs text-muted-foreground">Members moving to target ({movingMembers.length}):</div>
        <div className="mt-1">{movingMembers.map(m => m.first_name+" "+m.last_name).join(", ") || "—"}</div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={()=>onMerge(overrides())} data-testid="confirm-merge-btn"><Merge className="w-4 h-4 mr-1"/>Merge</Button>
      </DialogFooter>
    </div>
  );
}

function RestoreRow({ label, sub, when, onRestore, onUnmerge }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3 bg-secondary/40 rounded-lg" data-testid="restore-row">
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{label}</div>
        <div className="text-xs text-muted-foreground truncate">{sub} · {when?.slice(0,10)}</div>
      </div>
      <div className="flex gap-2">
        {onUnmerge && (
          <Button size="sm" variant="outline" onClick={onUnmerge} data-testid="unmerge-btn" title="Reverse this merge and restore original household">
            <GitBranch className="w-3 h-3 mr-1"/>Unmerge
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onRestore} data-testid="restore-btn"><Undo2 className="w-3 h-3 mr-1"/>Restore</Button>
      </div>
    </div>
  );
}
